import React, { useEffect, useState } from 'react';
import { FlatList, Image, Keyboard, Pressable } from "react-native";
import type { AccountProps, AccountSnoozeProps, CodeRequestProps, FocusPositionProps, ItemOrderProps, MyPlayerProps, PlayerDepositLimitProps, PlayerReferralProps, PromoProps, PublicPlayerProps, RewardOptionProps, WalletSettingsProps } from '../../types';
import { ItemOrderApi, ItemOrderHelpers, WalletApi } from '../api';
import { Checkbox, Icons } from '../../Components';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type DepositCardProps = {
    player:MyPlayerProps,
    wallet_settings:WalletSettingsProps,
    account:AccountProps,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    code_details?: {
        code_request?:CodeRequestProps,
        referrer?:PublicPlayerProps,
        promo?:PromoProps,
        player_referral?:PlayerReferralProps,
        reward_option?:RewardOptionProps
    },
    deposit_limit:PlayerDepositLimitProps,
    onTransact:(draft_order:ItemOrderProps, account_id?:string) => void,
    onCancel:() => void
}
const sections = [ 'header', 'input', 'limits' ]
const DepositCard = ({ wallet_settings, code_details, account, deposit_limit, onFocusPosition, onTransact, onCancel }:DepositCardProps) => {
    const Colors = useColors();
    const [ loaded, setLoaded ] = useState(false);
    const [ draft_order, setDraftOrder ] = useState<ItemOrderProps>();
    const [ instant_disclaimer, setInstantDisclaimer ] = useState(false);
    const [ account_snooze, setAccountSnooze ] = useState<AccountSnoozeProps>();

    useEffect(() => {
        getItemFromServer()
    },[])

    const getItemFromServer = async() => {
        const item = await ItemOrderApi.getItemByIdentifier('deposit')
        const as = await WalletApi.getSnoozeByAccountId(account.account_id);
        setAccountSnooze(as)
        setDraftOrder(ItemOrderHelpers.genDepositOrder(item, account))
        setLoaded(true)
    }

    const handleTransact = () => {
        if(!draft_order){ return }
        onTransact({ ...draft_order, total_amount: draft_order.amount as number }, account.account_id)
    }

    let errors = ItemOrderHelpers.isDepositOrdervalid(account, deposit_limit, wallet_settings, draft_order)
    if(account_snooze?.status == 'active'){ errors.push('This account is currently snoozing and cannot be used to deposit') }


    let order_amount = parseFloat(draft_order?.amount as string);
    let ach_daily_avail = deposit_limit.ach_daily_limit - deposit_limit.last_days_ach_deposits
    let card_daily_avail = deposit_limit.card_daily_limit - deposit_limit.last_days_card_deposits

    const instant_alert = errors.length == 0 && order_amount > deposit_limit.instant_available ? true : false
    const instant_confirmed = !instant_alert ? true : instant_alert && instant_disclaimer ? true : false

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:15 }}>
                        <View transparent style={{flex:1}}>
                            <Text size={20} theme='h1'>MAKE A DEPOSIT</Text>
                            <Text style={{ marginTop:5}} theme='description'>In order to participate in real-money activities, you will need to fund your BettorEdge Wallet.  Please enter the amount you wish to deposit below</Text>
                        </View>
                    </View>
                )
            case 'input':
                if(!draft_order){ return <></> }
                return (
                    <View nativeID='deposit_amount' style={{ justifyContent:'center', alignItems:'center', padding:20 }}>
                        <Text style={{ marginBottom:15 }} size={14} theme='description'>Minumum $25 / Maximum ${ItemOrderHelpers.getDepositAvailable(deposit_limit, account).toFixed(2)}</Text>
                        <TextInput 
                            style={{ flexGrow:1, width:200, textAlign:'center', color:Colors.text.success, fontSize:28 }}
                            placeholder='25'
                            onFocusPosition={onFocusPosition}
                            keyboardType='decimal-pad'
                            value={draft_order.amount as string}
                            onChangeText={(text) => setDraftOrder({ ...draft_order, amount:text })}
                        />
                        <Text style={{ marginTop:15 }} size={14} theme='warning' textAlign='center'>*All deposits are held for {deposit_limit.hold_days} days before they are eligible for withdraw</Text>

                    </View>
                )
            case 'limits':
                return (
                    <Pressable onPress={() => Keyboard.dismiss()}>
                        <View float style={{ margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', borderTopRightRadius:8, borderTopLeftRadius:8, padding:10 }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h1'>My Deposit Limits</Text>
                                </View>
                            </View>
                            <View transparent type='body'>
                                {account.account_type == 'ach' ? 
                                    <View type='row' style={{ padding:10 }}>
                                        <Text theme='description' style={{ flex:1 }}>ACH Daily Limit</Text>
                                        <Text theme='success'>${deposit_limit.ach_daily_limit.toFixed(2)}</Text>
                                    </View>
                                :<></>}
                                {account.account_type == 'ach' ? 
                                    <View type='row' style={{ padding:10, borderBottomWidth:1, borderColor:Colors.borders.light}}>
                                        <Text theme='description' style={{ flex:1 }}>Rolling 24 hour deposits</Text>
                                        <Text theme='error'>(${deposit_limit.last_days_ach_deposits.toFixed(2)})</Text>
                                    </View>
                                :<></>}
                                {account.account_type == 'ach' ? 
                                    <View type='row' style={{ padding:20 }}>
                                        <Text theme='h1' style={{ flex:1 }}>ACH Available</Text>
                                        <Text theme='h1' color={ach_daily_avail > 25 ? Colors.text.success : Colors.text.error}>${ach_daily_avail.toFixed(2)}</Text>
                                    </View>
                                :<></>}
                                {account.account_type != 'ach' ? 
                                    <View type='row' style={{ padding:10 }}>
                                        <Text theme='description' style={{ flex:1 }}>Daily Limit</Text>
                                        <Text theme='success'>${deposit_limit.card_daily_limit.toFixed(2)}</Text>
                                    </View>
                                :<></>}
                                {account.account_type != 'ach' ? 
                                    <View type='row' style={{ padding:10, borderBottomWidth:1, borderColor:Colors.borders.light}}>
                                        <Text theme='description' style={{ flex:1 }}>Rolling 24 hour deposits</Text>
                                        <Text theme='error'>(${deposit_limit.last_days_card_deposits.toFixed(2)})</Text>
                                    </View>
                                :<></>}
                                {account.account_type != 'ach' ? 
                                    <View type='row' style={{ padding:20 }}>
                                        <Text theme='h1' style={{ flex:1 }}>Available</Text>
                                        <Text theme='h1' color={card_daily_avail > 25 ? Colors.text.success : Colors.text.error}>${card_daily_avail.toFixed(2)}</Text>
                                    </View>
                                :<></>}
                            </View>
                            <View type='footer' style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                                <View transparent style={{ flex:1, marginRight:5 }}>
                                    <Text theme='h1'>Instant Available</Text>
                                    <Text style={{ marginTop:2 }} theme='description'>The amount that can be instantly available in your account</Text>
                                </View>
                                <Text theme='h1' color={deposit_limit.instant_available > 0 ? Colors.text.success : Colors.text.error}>${deposit_limit.instant_available.toFixed(2)}</Text>
                            </View>
                        </View>
                </Pressable>
                )
            case 'referral':
                if(!code_details?.code_request?.pending_deposit){ return <></> }
                if(!code_details.promo || !code_details.referrer){ return <></> }
                if(!['referral_code','referral_sweepstakes'].includes(code_details.promo.type)){ return <></> }
                return (
                        <View style={{ margin:15, flexDirection:'row' }}>
                            <View style={{ justifyContent:'center' }}>
                                {code_details.referrer?.profile_pic?
                                <Image
                                    source={{ uri: code_details.referrer.profile_pic }}
                                    style={{ height: 60, width:60, borderRadius:4 }}
                                    resizeMode='cover'
                                />
                                :
                                <Icons.GiftIcon size={40} color={Colors.text.gold} />
                                }
                            </View>
                            <View style={{flex:1, backgroundColor:Colors.absolutes.incentive.gold_faded, padding:10 }}>
                                <Text style={{ paddingBottom:10, borderBottomWidth:1, borderBottomColor:Colors.borders.light }} theme='h1'>Give back to {code_details.referrer?.username}</Text>
                                <Text style={{ paddingTop:5 }} size={12} theme='h2'>Make a qualifying deposit and {code_details.referrer.username} will receive a reward!</Text>
                            </View>
                        </View>
                )
            default: return <></>
        }
    }


    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'deposit_sections'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
                {instant_alert ?
                <View float nativeID='instant_available' style={{ borderBottomRightRadius:0, borderBottomLeftRadius:0 }}>
                    <View transparent style={{ borderTopRightRadius:8, borderTopLeftRadius:8, backgroundColor:Colors.text.warning, borderBottomWidth:1, borderBottomColor:Colors.borders.light  }}>
                        <View transparent style={{flexDirection:'row', padding:18 }}>
                            <Icons.AlertIcon color={Colors.text.white} size={16}/>
                            <Text theme='h1' style={{ marginLeft:10 }} size={14} color={Colors.text.white}>INSTANT DEPOSIT NOTICE</Text>
                        </View>
                    </View>
                    <View transparent style={{ padding:5 }}>
                        <View transparent style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                            <Text style={{ flex:1 }} size={14} theme='h2'>Available Instantly</Text>
                            <Text size={14} theme='h1'>${deposit_limit.instant_available.toFixed(2)}</Text>
                        </View>
                        <View transparent style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                            <Text style={{ flex:1 }} theme='h2'>Available after settlement ({deposit_limit.hold_days} days)</Text>
                            <Text size={14} theme='h1'>${(order_amount - deposit_limit.instant_available).toFixed(2)}</Text>
                        </View>
                        <View transparent style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                            <Text style={{ flex:1 }} theme='h2'>Total Deposit</Text>
                            <Text size={14} theme='h1'>${order_amount.toFixed(2)}</Text>
                        </View>
                    </View>
                    <Button style={{ borderRadius:0, flexDirection:'row', alignItems:'center', padding:14, backgroundColor:Colors.views.footer }} onPress={() => setInstantDisclaimer(!instant_disclaimer)}>
                        <Checkbox color={Colors.text.action} disabled checked={instant_disclaimer} onSelect={() => console.log('')} />
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text size={14} color={Colors.text.action} theme='description'>I understand that ${deposit_limit.instant_available.toFixed(2)} will instantly be added and ${(order_amount - deposit_limit.instant_available).toFixed(2)} will be added after {deposit_limit.hold_days} days.</Text>
                        </View>
                    </Button>
                </View>
                :<></>}
                {errors.length > 0 && loaded ?
                <View float nativeID='errors' style={{ borderBottomRightRadius:0, borderBottomLeftRadius:0 }}>
                    <View transparent style={{ flexDirection:'row', alignItems:'center', padding:15, borderTopRightRadius:8, borderTopLeftRadius:8, backgroundColor:Colors.text.error, borderBottomWidth:1, borderBottomColor:Colors.borders.light  }}>
                        <Icons.AlertIcon color={Colors.text.white} size={24}/>
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1' size={14} color={Colors.text.white}>ERRORS</Text>
                            <Text style={{ marginTop: 3}} theme='description' color={Colors.text.white}>Please fix the following before continuing</Text>
                        </View>
                    </View>
                    <View transparent style={{ padding:10 }}>
                    {errors.map(e => {
                        return (
                            <Text size={14} theme='h2' color={Colors.text.error}>{e}</Text>
                        )
                    })}
                    </View>
                </View>
                :<></>}
            </View>
            <View type='footer' style={{ flexDirection:'row', padding:20 }}>
                <Button
                    style={{ flex:1, marginRight:5, opacity: 1 }}
                    title={'CANCEL'}
                    type='close'
                    onPress={() => onCancel()}
                />
                <Button
                    style={{ flex:3, opacity: errors.length > 0 || !instant_confirmed ? 0.5: 1 }}
                    disabled={errors.length > 0 || !instant_confirmed ? true : false}
                    title={'REVIEW DEPOSIT'}
                    type='success'
                    onPress={() => handleTransact()}
                />
            </View>
        </View>
    )
}

export default DepositCard

